import React, { useEffect, useState } from 'react';
import ProfileHighlights from '@/components/profile/ProfileHighlights';
import AboutMeTab from '@/components/profile/AboutMeTab';
import MySocialMattTab from '@/components/profile/MySocialMattTab';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { EmptyState } from '@/components/ui/EmptyState';
import { User, AlertCircle } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

const MyJourney: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [profile, setProfile] = useState<any>(null);
  const [academies, setAcademies] = useState<any[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [favoriteTechniques, setFavoriteTechniques] = useState<Record<string, string[]>>({});
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [techniqueCategories, setTechniqueCategories] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    if (!user) {
      return;
    }
    // Fetch profile (with join for business_types)
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*, business_type:business_types(name)')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      console.error('MyJourney - Profile fetch error:', profileError);
    }

    if (!profileData) {
      setLoading(false);
      return;
    }

    setProfile(profileData);

    // Fetch main linked academy
    let academyArr = [];
    
    if (profileData?.main_academy_id) {
      const { data: academyData, error: academyError } = await supabase
        .from('academies')
        .select('id, name')
        .eq('id', profileData.main_academy_id)
        .limit(1);
      
      if (academyError) {
        console.error('MyJourney - Academy fetch error:', academyError);
      }
      
      academyArr = academyData || [];
    }
    
    setAcademies(academyArr);

    // Fetch interests
    const { data: hobbyData } = await supabase
      .from('profile_hobbies')
      .select('hobby:hobbies(name)')
      .eq('profile_id', user.id);
    setInterests(hobbyData?.map((h: any) => h.hobby?.name).filter(Boolean) || []);

    // Fetch real technique categories
    const { data: categoriesData } = await supabase
      .from('technique_categories')
      .select('name');
    const categories = categoriesData?.map((c: any) => c.name) || [];
    setTechniqueCategories(categories);

    // Fetch favorite techniques grouped by category
    const { data: favTechData } = await supabase
      .from('profile_favorite_techniques')
      .select('technique:techniques(name, category:technique_categories(name))')
      .eq('profile_id', user.id);
    const grouped: Record<string, string[]> = {};
    categories.forEach((cat: string) => { grouped[cat] = []; });
    favTechData?.forEach((item: any) => {
      const cat = item.technique?.category?.name || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      if (item.technique?.name) grouped[cat].push(item.technique.name);
    });
    setFavoriteTechniques(grouped);

    // Fetch user's posts
    const { data: postData } = await supabase
      .from('posts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);
    setPosts(postData || []);

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white text-lg">
        Loading profile...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#160C0C]">
        <TopNavigation />
        <div className="pt-20 pb-20">
          <EmptyState
            icon={AlertCircle}
            title={t('profile.not_found')}
            subtitle={t('profile.not_found_description')}
            className="text-white"
          />
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <>
      <TopNavigation />
      <div className="relative min-h-screen w-full bg-[#160C0C] pb-20 pt-20">
        <div className="px-4">
          <ProfileHighlights
            fullName={profile.full_name || ''}
            username={profile.username || ''}
            belt={profile.belt_rank || ''}
            beltColor={profile.belt_color || '#fff'}
            academies={academies}
            location={{ country: profile.country, state: profile.state, city: profile.city }}
            social={{ instagram: profile.instagram, x: profile.x, facebook: profile.facebook, youtube: profile.youtube }}
            website={profile.website}
            interests={interests}
            profession={profile.profession || ''}
            favoriteTechniques={favoriteTechniques}
            techniqueCategories={techniqueCategories}
            businessType={profile.business_type?.name}
            linkedin={profile.linkedin}
            email={user.email}
            profilePictureUrl={profile.profile_picture_url}
            userId={user.id}
            isOwnProfile={true}
            editable={true}
            onProfileUpdated={fetchData}
            isInstructor={profile.is_instructor || false}
          />
        </div>
        <BottomNavigation />
      </div>
    </>
  );
};

export default MyJourney; 